/**
 * "NO EFFECT": what the ranger is told when a blast of his struck something it cannot open (INTEG3, ROUND7).
 * Split out of damage.ts, which re-exports `REFUSED` and `isRefusal` for the HUD and the sound bank.
 */

import type { Ctx } from './ctx';
import { PROPS, weakSideOf } from './props';
import { INTEG3 } from './tuning';
import type { PropDef } from './types';

/**
 * A refusal travels as a `pickup` so it reaches the banner, but it is the opposite of one. The prefix is
 * declared here, where the words are built, because two layers have to recognise it and neither may guess:
 * the HUD paints it red-amber (`ui/hud/banners.ts`) and the bank gives it a dull knock rather than the
 * amber blip that means a reward (`audio/eventMap.ts`).
 */
export const REFUSED = 'NO EFFECT';
export const isRefusal = (what: string): boolean => what.startsWith(REFUSED);

/** What a grenade against a wall is told (ROUND7 §4): the wall is `hard`, and the standing orders say so too. */
export const WALL_REFUSAL = `${REFUSED} - A WALL NEEDS A LAW OR A BOMB`;

/** Say `what` about this prop - only for the ranger's own blast, and once per prop every few seconds, not per blast. */
function refuse(ctx: Ctx, prop: PropDef, byPlayer: boolean, what: string): void {
  const state = ctx.world.props.find((p) => p.id === prop.id);
  if (!byPlayer || !state || state.destroyed) return;
  if (state.refusedTick !== undefined && ctx.world.tick - state.refusedTick < INTEG3.blastRefusedEveryTicks) return;
  state.refusedTick = ctx.world.tick;
  ctx.events.push({ type: 'pickup', what });
}

/**
 * A blast that would have destroyed this prop struck the wrong face (`PropSpec.weakSide`): nothing happens, and
 * the ranger - if it was his - is told where the way in is.
 */
export function refuseBlast(ctx: Ctx, prop: PropDef, byPlayer: boolean): void {
  const side = weakSideOf(prop);
  if (side === null) return;
  const point = PROPS[prop.kind].weakPoint ?? 'WEAK POINT';
  refuse(ctx, prop, byPlayer, `${REFUSED} - THE ${point} IS ON THE ${side.toUpperCase()} FACE`);
}

/**
 * His grenade went off against a wall. It used to do nothing and say nothing, and a player who has been told a
 * wall can be breached throws grenades at it until he runs out.
 */
export function refuseWall(ctx: Ctx, prop: PropDef, byPlayer: boolean): void {
  refuse(ctx, prop, byPlayer, WALL_REFUSAL);
}
